import { Loader2, X } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { chatLucideIconProps } from '../chat-ui';
import { VOICE_DURATION_WIDTH_PX } from './constants';
import { formatVoiceDuration } from './utils';
import { ChatComposerVoiceWaveformCanvas } from './voice-waveform-canvas';

type VoiceActivityPhase = 'recording' | 'transcribing';

function VoiceControlButton({
  onClick,
  ariaLabel,
  children,
}: {
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-muted/65 text-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function ChatComposerVoiceActivity({
  phase,
  duration,
  liveLevelRef,
  onCancel,
  onStopRecording,
}: {
  phase: VoiceActivityPhase;
  duration: number;
  liveLevelRef: MutableRefObject<number>;
  onCancel: () => void;
  onStopRecording: () => void;
}) {
  const isRecording = phase === 'recording';
  const waveformViewportRef = useRef<HTMLDivElement | null>(null);
  const [waveformViewportWidth, setWaveformViewportWidth] = useState<
    number | null
  >(null);

  useEffect(() => {
    const element = waveformViewportRef.current;
    if (!element) {
      return;
    }

    const updateViewportWidth = (nextWidth?: number) => {
      const measuredWidth = Math.round(nextWidth ?? element.clientWidth);
      setWaveformViewportWidth(current =>
        current === measuredWidth ? current : measuredWidth
      );
    };

    updateViewportWidth();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(entries => {
      updateViewportWidth(entries[0]?.contentRect.width);
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <VoiceControlButton
        onClick={onCancel}
        ariaLabel={
          isRecording ? 'Cancel voice recording' : 'Cancel voice transcription'
        }
      >
        <X {...chatLucideIconProps} className="size-4" />
      </VoiceControlButton>

      <div ref={waveformViewportRef} className="min-w-0 flex-1 text-foreground">
        <ChatComposerVoiceWaveformCanvas
          phase={phase}
          liveLevelRef={liveLevelRef}
          viewportWidth={waveformViewportWidth}
        />
      </div>

      <div
        className="chat-type-caption shrink-0 text-right font-medium tabular-nums text-muted-foreground"
        style={{ width: `${VOICE_DURATION_WIDTH_PX}px` }}
      >
        {formatVoiceDuration(duration)}
      </div>

      {isRecording ? (
        <VoiceControlButton
          onClick={onStopRecording}
          ariaLabel="Stop recording"
        >
          <span
            aria-hidden="true"
            className="block size-2.5 rounded-[2px] bg-current"
          />
        </VoiceControlButton>
      ) : (
        <div className="flex size-8 shrink-0 items-center justify-center text-muted-foreground">
          <Loader2 {...chatLucideIconProps} className="size-4 animate-spin" />
        </div>
      )}
    </div>
  );
}
